/* App-level poller for the server-side multi-cost-center Actuals export. Mounted once per tab, in
 * XptTopNavigation, so progress survives navigation between xPT pages.
 *
 * 1. On mount, reconcile with `GET /actuals-exports/active` (the server is the source of truth).
 * 2. While an export is queued or running, the tabs elect one visible leader
 *    (ActualsExportTabCoordinator). Only the leader polls: every 5 s for the first minute after the
 *    request, then every 10 s. Polling pauses while the tab is hidden.
 * 3. On READY the leader fetches a fresh presigned URL, checks that it points to the exports bucket,
 *    and navigates to it. The other tabs pick up the new state from localStorage and report it.
 * 4. App-level flash messages report progress, the result and failures. The progress message offers
 *    Abort; any tab may abort, not just the leader.
 *
 * Nothing here logs response bodies or URLs.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logger } from 'src/analytics/KatalLogger';
import { useFlashMessage } from 'src/app/AppBannerContext';
import { ACTUALS_EXPORT_MESSAGES, ACTUALS_EXPORT_PROGRESS_FLASH_ID, ACTUALS_EXPORT_RESULT_FLASH_ID } from 'src/constants/actuals-export-constants';
import { eLocalStorageKeys } from 'src/constants/generic-constants';
import type { ActualsExport, ActualsExportStatus, ActualsExportTrackingState } from 'src/models/ActualsExportModels';
import { actualsExportService, isActualsExportApiError } from 'src/services/actuals-export/ActualsExportService';
import { ActualsExportTabCoordinator } from 'src/services/actuals-export/ActualsExportTabCoordinator';
import {
  actualsExportCleared,
  actualsExportSyncedFromOtherTab,
  actualsExportTracked,
  loadPersistedActualsExport,
  selectActualsExport
} from 'src/store/slices/actualsExportSlice';
import type { AppDispatch } from 'src/store/store';
import {
  EMPTY_ACTUALS_EXPORT_TRACKING_STATE,
  formatCostCenterCount,
  getActualsExportErrorMessage,
  getActualsExportFailureMessage,
  getActualsExportPollIntervalMs,
  isAbortedActualsExport,
  isActualsExportInProgress,
  isAllowedActualsExportDownloadUrl,
  toActualsExportTrackingState
} from 'src/utils/actuals-export-utils';
import { assignWindowLocation } from 'src/utils/navigation-utils';
import { getActualsExportsBucketName } from 'src/utils/xpt-s3-bucket-details';

const isSameTrackingState = (a: ActualsExportTrackingState, b: ActualsExportTrackingState): boolean =>
  a.exportId === b.exportId &&
  a.status === b.status &&
  a.ccCount === b.ccCount &&
  a.fileName === b.fileName &&
  a.requestedAt === b.requestedAt &&
  a.errorCode === b.errorCode;

const isFinished = (status: ActualsExportStatus | null): boolean => status === 'READY' || status === 'FAILED' || status === 'EXPIRED';

interface ExportFlashActions {
  /** Fetches a fresh URL, validates it and navigates to it. Returns true when the download started. */
  startDownload: (exportId: string, ccCount: number | null) => Promise<boolean>;
  showReady: (exportId: string, ccCount: number | null, content: string) => void;
  showFailed: (status: ActualsExportStatus | null, errorCode: string | null) => void;
  /** Tracks a finished export, tells the other tabs, and reports it unless another tab already did. */
  reportFinished: (latest: ActualsExport) => Promise<void>;
  /** Aborts the export; on a NOT_IN_PROGRESS race it refreshes the status instead. */
  abortExport: (exportId: string) => Promise<void>;
}

export const useActualsExportPoller = (isEnabled: boolean): void => {
  const dispatch = useDispatch<AppDispatch>();
  const trackedExport = useSelector(selectActualsExport);
  const { addMessage, removeMessage } = useFlashMessage();
  const [isLeader, setIsLeader] = useState(false);
  const [isVisible, setIsVisible] = useState<boolean>(() => !document.hidden);
  const coordinatorRef = useRef<ActualsExportTabCoordinator | null>(null);
  // True while an abort request is in flight, so re-clicks of the Abort action are ignored.
  const abortInFlightRef = useRef(false);

  // The latest state for timers and event handlers; updated before every dispatch made here.
  const trackedRef = useRef<ActualsExportTrackingState>(trackedExport);
  trackedRef.current = trackedExport;

  const { exportId, status, ccCount } = trackedExport;
  const inProgress = isActualsExportInProgress(status);

  const track = useCallback(
    (next: ActualsExportTrackingState | null): void => {
      trackedRef.current = next ?? EMPTY_ACTUALS_EXPORT_TRACKING_STATE;
      dispatch(next ? actualsExportTracked(next) : actualsExportCleared());
    },
    [dispatch]
  );

  const actions = useMemo((): ExportFlashActions => {
    const showDownloadFailed = (content: string, retry: (() => void) | null): void => {
      addMessage({
        id: ACTUALS_EXPORT_RESULT_FLASH_ID,
        type: 'error',
        dismissible: true,
        header: ACTUALS_EXPORT_MESSAGES.DOWNLOAD_FAILED_HEADER,
        content,
        ...(retry ? { actionText: ACTUALS_EXPORT_MESSAGES.DOWNLOAD_AGAIN, onAction: retry } : {})
      });
    };

    const startDownload = async (targetExportId: string, targetCcCount: number | null): Promise<boolean> => {
      const downloadAgain = (): void => void startDownload(targetExportId, targetCcCount);
      try {
        const { url } = await actualsExportService.getDownloadUrl(targetExportId);
        if (!isAllowedActualsExportDownloadUrl(url, getActualsExportsBucketName())) {
          logger.error('Multi-cost-center export download link rejected');
          showDownloadFailed(ACTUALS_EXPORT_MESSAGES.DOWNLOAD_URL_REJECTED, null);
          return false;
        }
        assignWindowLocation(url);
        return true;
      } catch (error) {
        const code = isActualsExportApiError(error) ? error.code : 'UNKNOWN';
        logger.warn('Multi-cost-center export download failed', { code });
        showDownloadFailed(getActualsExportErrorMessage(code), code === 'EXPIRED' || code === 'NOT_FOUND' ? null : downloadAgain);
        return false;
      }
    };

    const showReady = (targetExportId: string, targetCcCount: number | null, content: string): void => {
      addMessage({
        id: ACTUALS_EXPORT_RESULT_FLASH_ID,
        type: 'success',
        dismissible: true,
        header: ACTUALS_EXPORT_MESSAGES.READY_HEADER(formatCostCenterCount(targetCcCount)),
        content,
        actionText: ACTUALS_EXPORT_MESSAGES.DOWNLOAD_AGAIN,
        onAction: () => void startDownload(targetExportId, targetCcCount)
      });
    };

    const retry = async (): Promise<void> => {
      removeMessage(ACTUALS_EXPORT_RESULT_FLASH_ID);
      try {
        const created = await actualsExportService.retryLastExport();
        track(toActualsExportTrackingState(created));
      } catch (error) {
        const apiError = isActualsExportApiError(error) ? error : null;
        if (apiError?.activeExport) {
          track(toActualsExportTrackingState(apiError.activeExport));
        }
        logger.warn('Multi-cost-center export retry failed', { code: apiError?.code ?? 'UNKNOWN' });
        addMessage({
          id: ACTUALS_EXPORT_RESULT_FLASH_ID,
          type: 'error',
          dismissible: true,
          header: ACTUALS_EXPORT_MESSAGES.FAILED_HEADER,
          content: getActualsExportErrorMessage(apiError?.code ?? 'UNKNOWN')
        });
      }
    };

    const showFailed = (failedStatus: ActualsExportStatus | null, errorCode: string | null): void => {
      // An aborted export is not an error: the user asked for it, so there is nothing to retry or download.
      if (isAbortedActualsExport(failedStatus, errorCode)) {
        addMessage({
          id: ACTUALS_EXPORT_RESULT_FLASH_ID,
          type: 'info',
          dismissible: true,
          header: ACTUALS_EXPORT_MESSAGES.ABORTED_HEADER,
          content: ACTUALS_EXPORT_MESSAGES.ABORTED_CONTENT
        });
        return;
      }
      // Retry re-sends this tab's last request; a tab that did not send one has nothing to retry.
      const canRetry = actualsExportService.hasRetryableRequest();
      addMessage({
        id: ACTUALS_EXPORT_RESULT_FLASH_ID,
        type: 'error',
        dismissible: true,
        header: ACTUALS_EXPORT_MESSAGES.FAILED_HEADER,
        content: getActualsExportFailureMessage(failedStatus, errorCode),
        ...(canRetry ? { actionText: ACTUALS_EXPORT_MESSAGES.RETRY, onAction: () => void retry() } : {})
      });
    };

    const reportFinished = async (latest: ActualsExport): Promise<void> => {
      const coordinator = coordinatorRef.current;
      // Another tab may have finished it already (its storage event is still on the way).
      const persisted = loadPersistedActualsExport();
      const reportedElsewhere = persisted.exportId === latest.exportId && isFinished(persisted.status);
      track(toActualsExportTrackingState(latest));
      coordinator?.broadcastExportUpdated();
      if (reportedElsewhere) {
        return;
      }
      if (latest.status === 'READY') {
        if (await startDownload(latest.exportId, latest.costCenterCount)) {
          showReady(latest.exportId, latest.costCenterCount, ACTUALS_EXPORT_MESSAGES.READY_CONTENT(latest.fileName));
        }
      } else {
        showFailed(latest.status, latest.errorCode);
      }
    };

    /** Tracks the export the service returned; a job that already finished goes through reportFinished. */
    const applyExport = async (latest: ActualsExport): Promise<void> => {
      if (isActualsExportInProgress(latest.status)) {
        track(toActualsExportTrackingState(latest));
        coordinatorRef.current?.broadcastExportUpdated();
        return;
      }
      await reportFinished(latest);
    };

    const abortExport = async (targetExportId: string): Promise<void> => {
      if (abortInFlightRef.current) {
        return;
      }
      abortInFlightRef.current = true;
      try {
        await applyExport(await actualsExportService.abortExport(targetExportId));
      } catch (error) {
        const apiError = isActualsExportApiError(error) ? error : null;
        if (apiError?.code === 'NOT_IN_PROGRESS') {
          // The job finished before the abort reached it: refresh the status and let the normal
          // READY/FAILED handling run.
          try {
            await applyExport(await actualsExportService.getExport(targetExportId));
          } catch (refreshError) {
            logger.warn('Multi-cost-center export refresh after abort failed', {
              code: isActualsExportApiError(refreshError) ? refreshError.code : 'UNKNOWN'
            });
          }
          return;
        }
        logger.warn('Multi-cost-center export abort failed', { code: apiError?.code ?? 'UNKNOWN', status: apiError?.status ?? 0 });
      } finally {
        abortInFlightRef.current = false;
      }
    };

    return { startDownload, showReady, showFailed, reportFinished, abortExport };
  }, [addMessage, removeMessage, track]);

  /** Applies the state another tab persisted, and reports an export that tab finished. */
  const syncFromOtherTab = useCallback((): void => {
    const next = loadPersistedActualsExport();
    const previous = trackedRef.current;
    if (isSameTrackingState(previous, next)) {
      return;
    }
    trackedRef.current = next;
    dispatch(actualsExportSyncedFromOtherTab(next));
    if (next.exportId && previous.exportId === next.exportId && isActualsExportInProgress(previous.status)) {
      if (next.status === 'READY') {
        actions.showReady(next.exportId, next.ccCount, ACTUALS_EXPORT_MESSAGES.READY_IN_OTHER_TAB_CONTENT);
      } else if (isFinished(next.status)) {
        actions.showFailed(next.status, next.errorCode);
      }
    }
  }, [dispatch, actions]);
  const syncRef = useRef(syncFromOtherTab);
  syncRef.current = syncFromOtherTab;

  // 1. Reconcile with the server once enabled.
  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    let cancelled = false;
    actualsExportService
      .getActiveExport()
      .then((active) => {
        if (cancelled || !active) {
          return;
        }
        const next = toActualsExportTrackingState(active);
        if (!isSameTrackingState(trackedRef.current, next)) {
          track(next);
        }
      })
      .catch((error: unknown) => {
        logger.warn('Multi-cost-center export reconcile failed', { code: isActualsExportApiError(error) ? error.code : 'UNKNOWN' });
      });
    return () => {
      cancelled = true;
    };
  }, [isEnabled, track]);

  // 2. Progress message, in every tab.
  useEffect(() => {
    if (!isEnabled) {
      // The feature can turn off at runtime (flag off, S&M access lost): no poller runs any more, so
      // clear both messages instead of leaving a stale "Preparing…" or result banner on screen.
      removeMessage(ACTUALS_EXPORT_PROGRESS_FLASH_ID);
      removeMessage(ACTUALS_EXPORT_RESULT_FLASH_ID);
      return;
    }
    if (inProgress) {
      // A new export replaces the result message of the previous one.
      removeMessage(ACTUALS_EXPORT_RESULT_FLASH_ID);
      addMessage({
        id: ACTUALS_EXPORT_PROGRESS_FLASH_ID,
        type: 'info',
        loading: true,
        dismissible: true,
        header: ACTUALS_EXPORT_MESSAGES.PREPARING_HEADER(formatCostCenterCount(ccCount)),
        content: ACTUALS_EXPORT_MESSAGES.PREPARING_CONTENT,
        ...(exportId ? { actionText: ACTUALS_EXPORT_MESSAGES.ABORT, onAction: () => void actions.abortExport(exportId) } : {})
      });
    } else {
      removeMessage(ACTUALS_EXPORT_PROGRESS_FLASH_ID);
    }
  }, [isEnabled, inProgress, exportId, ccCount, addMessage, removeMessage, actions]);

  // 3. Tab visibility and changes persisted by other tabs.
  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    const onVisibilityChange = (): void => setIsVisible(!document.hidden);
    const onStorage = (event: StorageEvent): void => {
      if (event.key === eLocalStorageKeys.ActualsExport) {
        syncRef.current();
      }
    };
    onVisibilityChange();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('storage', onStorage);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [isEnabled]);

  // 4. Leader election while an export is in progress.
  useEffect(() => {
    if (!isEnabled || !inProgress) {
      return;
    }
    const coordinator = new ActualsExportTabCoordinator({
      onLeadershipChange: setIsLeader,
      onExportUpdated: () => syncRef.current()
    });
    coordinatorRef.current = coordinator;
    coordinator.start(!document.hidden);
    return () => {
      coordinator.stop();
      coordinatorRef.current = null;
      setIsLeader(false);
    };
  }, [isEnabled, inProgress]);

  useEffect(() => {
    coordinatorRef.current?.setVisible(isVisible);
  }, [isVisible]);

  // 5. Poll, in the visible leader tab only.
  useEffect(() => {
    if (!isEnabled || !isLeader || !isVisible || !inProgress || !exportId) {
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async (): Promise<void> => {
      let latest: ActualsExport;
      try {
        latest = await actualsExportService.getExport(exportId);
      } catch (error) {
        if (cancelled) {
          return;
        }
        const apiError = isActualsExportApiError(error) ? error : null;
        if (apiError?.status === 404) {
          // Not this user's export any more (for example another user signed in on this browser).
          const coordinator = coordinatorRef.current;
          track(null);
          coordinator?.broadcastExportUpdated();
          return;
        }
        logger.warn('Multi-cost-center export status check failed', { code: apiError?.code ?? 'UNKNOWN', status: apiError?.status ?? 0 });
        scheduleNext();
        return;
      }
      if (cancelled) {
        return;
      }
      if (isActualsExportInProgress(latest.status)) {
        const next = toActualsExportTrackingState(latest);
        if (!isSameTrackingState(trackedRef.current, next)) {
          track(next);
        }
        scheduleNext();
        return;
      }
      await actions.reportFinished(latest);
    };

    const scheduleNext = (): void => {
      timer = setTimeout(() => void poll(), getActualsExportPollIntervalMs(trackedRef.current.requestedAt, Date.now()));
    };

    scheduleNext();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isEnabled, isLeader, isVisible, inProgress, exportId, actions, track]);
};
